import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import GenericFilter from "./GenericFilter.jsx";
import { useCart } from "../Context/Cartcontext.jsx";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./Category.css";
import { getImageUrl } from "../utils/imageUtils";

const DynamicCategory = () => {
  const { categoryName } = useParams();
  const [products, setProducts] = useState([]);
  const [priceRange, setPriceRange] = useState(2000000);
  const [filters, setFilters] = useState({
    availability: [],
    condition: [], // Added condition filter
    brand: [],
    model: [],
  });
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [categoryDisplayName, setCategoryDisplayName] = useState("");
  const [categoryNotFound, setCategoryNotFound] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const itemsPerPage = 12;

  const navigate = useNavigate();
  const { addToCart } = useCart();
const baseURL="https://backendweb.properteck.lk";

  // Helper function to get product availability status
  const getProductAvailability = (product) => {
    if (product.isPreOrder) return "Pre-Order";
    if (product.stock > 0) return "In Stock";
    return "Out of Stock";
  };

  // Helper function to get product price for filtering
  const getProductPrice = (product) => {
    if (product.type === 'special_offer' || product.type === 'new_arrival') {
      return product.offerPrice || product.price;
    }
    return product.price;
  };

  const formatCategoryName = (urlName) => {
    return urlName
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setCategoryNotFound(false);

      const res = await axios.get(`${baseURL}/api/products/getproducts`);
      
      const formattedCategoryName = formatCategoryName(categoryName);
      
      const categoryProducts = res.data.products.filter((product) => {
        const productCategory = product.category?.toLowerCase();
        const urlCategory = categoryName.toLowerCase();
        const formattedCategory = formattedCategoryName.toLowerCase();
        
        return productCategory === urlCategory || 
               productCategory === formattedCategory ||
               productCategory === urlCategory.replace(/-/g, ' ') ||
               productCategory === urlCategory.replace(/-/g, '');
      });
      
      if (categoryProducts.length === 0) {
        const categoriesRes = await axios.get(`${baseURL}/api/categories/getcategories`);
        const validCategories = categoriesRes.data.categories || categoriesRes.data || [];
        
        const isValidCategory = validCategories.some(cat => {
          const catName = cat.name.toLowerCase();
          const urlCat = categoryName.toLowerCase().replace(/-/g, ' ');
          return catName === urlCat || catName === categoryName.toLowerCase();
        });
        
        if (!isValidCategory) {
          setCategoryNotFound(true);
        }
      }
      
      setProducts(categoryProducts);
      setCategoryDisplayName(formattedCategoryName);
    } catch (error) {
      console.error("Failed to fetch products", error);
      toast.error("Failed to load products", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (categoryName) {
      fetchProducts();
      setCurrentPage(1);
      setFilters({
        availability: [],
        condition: [], // Added condition filter
        brand: [],
        model: [],
      });
      setPriceRange(2000000);
    }
  }, [categoryName]);

  const handleItemClick = (product) => {
    if (product.type === 'new_arrival') {
      navigate(`/newarrivals/${product._id}`);
    } else if (product.type === 'special_offer') {
      navigate(`/offer/${product._id}`);
    } else {
      navigate(`/item/${product._id}`);
    }
  };

  const onFilterChange = (newFilters, newPriceRange) => {
    setFilters(newFilters);
    setPriceRange(newPriceRange);
    setCurrentPage(1);
  };

  const handleAddToCart = (product) => {
    // Don't allow adding pre-order items to cart
    if (product.isPreOrder) {
      toast.info(`${product.name} is available for pre-order only. Please contact us for pre-order details.`, {
        position: "top-right",
        autoClose: 4000,
      });
      return;
    }

    const cartItem = {
      id: product._id,
      name: product.name,
      imageUrl: product.imageUrl,
      type: product.type || "item",
      condition: product.condition || "brandnew",
    };

    if (product.type === 'special_offer' && product.offerPrice) {
      cartItem.price = product.offerPrice;
      cartItem.regularPrice = product.regularPrice;
      cartItem.offerPrice = product.offerPrice;
    } else if (product.type === 'new_arrival' && product.offerPrice) {
      cartItem.price = product.offerPrice;
      cartItem.regularPrice = product.regularPrice;
      cartItem.offerPrice = product.offerPrice;
    } else {
      cartItem.price = product.price;
    }

    addToCart(cartItem);

    toast.success(`${product.name} added to cart!`, {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };

  // Updated filtering logic
  const filteredProducts = products.filter((product) => {
    // Price filter
    const productPrice = getProductPrice(product);
    if (productPrice > priceRange) return false;

    // Availability filter
    if (filters.availability && filters.availability.length > 0) {
      const productAvailability = getProductAvailability(product);
      if (!filters.availability.includes(productAvailability)) return false;
    }

    // Condition filter
    if (filters.condition && filters.condition.length > 0) {
      if (!filters.condition.includes(product.condition || 'brandnew')) return false;
    }

    // Brand filter
    if (filters.brand && filters.brand.length > 0) {
      if (!filters.brand.includes(product.brand || "Unknown")) return false;
    }

    // Model filter
    if (filters.model && filters.model.length > 0) {
      if (!filters.model.includes(product.model || "Unknown")) return false;
    }

    return true;
  });

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (categoryNotFound && !loading) {
      const timer = setTimeout(() => {
        navigate('/');
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [categoryNotFound, loading, navigate]);

if (categoryNotFound && !loading) {
  return (
    <div className="dc-category-not-found">
      <h2>Category Not Found</h2>
      <p>The category "{categoryName}" doesn't exist.</p>
      <button 
        onClick={() => navigate('/')}
        className="dc-go-home-btn"
      >
        Go Back to Home
      </button>
    </div>
  );
}
  return (
    <div className="dc-container">
      <div className="dc-main">
        <h3>{categoryDisplayName.toUpperCase()}</h3>
        <button
          className="dc-filter-toggle-btn"
          onClick={() => setFilterOpen(true)}
        >
          &#9776; Filters
        </button>
      </div>

      <div className="dc-main-container">
        <div className={`dc-filter-sidebar${filterOpen ? " dc-open" : ""}`}>
          <button
            className="dc-filter-close-btn"
            onClick={() => setFilterOpen(false)}
            aria-label="Close filters"
          >
            &times;
          </button>
          <GenericFilter
            products={products}
            priceRange={priceRange}
            filters={filters}
            onFilterChange={onFilterChange}
          />
        </div>
        {filterOpen && (
          <div
            className="dc-filter-overlay"
            onClick={() => setFilterOpen(false)}
          />
        )}
        <div className="dc-product-grid">
          {loading ? (
            <div className="dc-loading">
              <div className="dc-loading-content">
                <div className="dc-loading-spinner"></div>
                Loading {categoryDisplayName} products...
              </div>
            </div>
          ) : currentProducts.length === 0 ? (
            <div className="dc-no-products">
              <div className="dc-no-products-content">
                <h3>No {categoryDisplayName} products found</h3>
                <p>Try adjusting your filters or check back later.</p>
                {(filters.availability.length > 0 || filters.condition.length > 0 || 
                  filters.brand.length > 0 || filters.model.length > 0 || priceRange < 2000000) && (
                  <button 
                    onClick={() => {
                      setFilters({ availability: [], condition: [], brand: [], model: [] });
                      setPriceRange(2000000);
                    }}
                    className="dc-clear-filters-btn"
                  >
                    Clear All Filters
                  </button>
                )}
              </div>
            </div>
          ) : (
            currentProducts.map((product) => (
              <div
                key={product._id}
                className="dc-product-card"
                onClick={() => handleItemClick(product)}
              >
                {/* Product badges container */}
                <div className="dc-product-badges">
                  {/* Special Offer Badge */}
                  {product.type === 'special_offer' && product.discount && (
                    <div className="dc-product-type-indicator dc-special-offer-badge">
                      <span className="dc-discount-percentage">{product.discount}%</span>
                      <span className="dc-discount-text">OFF</span>
                    </div>
                  )}
                  
                  {/* New Arrival Badge */}
                  {product.type === 'new_arrival' && (
                    <div className="dc-product-type-indicator dc-new-arrival-badge">
                      <svg className="dc-star-icon" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                      </svg>
                      <span className="dc-new-text">NEW</span>
                    </div>
                  )}

                  {/* Used Condition Badge */}
                  {product.condition === 'used' && (
                    <div className="dc-product-type-indicator dc-used-badge">
                      <svg className="dc-recycle-icon" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 6V4L8 8L12 12V10C15.31 10 18 12.69 18 16S15.31 22 12 22S6 19.31 6 16H4C4 20.42 7.58 24 12 24S20 20.42 20 16S16.42 6 12 6Z"/>
                      </svg>
                      <span className="dc-used-text">USED</span>
                    </div>
                  )}

                  {/* Pre-order Badge */}
                  {product.isPreOrder && (
                    <div className="dc-product-type-indicator dc-preorder-badge">
                      <svg className="dc-clock-icon" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2M12 20C7.59 20 4 16.41 4 12S7.59 4 12 4 20 7.59 20 12 16.41 20 12 20M12.5 7V12.25L17 14.92L16.25 16.15L11 13V7H12.5Z"/>
                      </svg>
                      <span className="dc-preorder-text">PRE-ORDER</span>
                    </div>
                  )}
                </div>

                <img
                  src={getImageUrl(product.imageUrl)}
                  alt={product.name}
                  className="dc-product-img"
                  onError={(e) => {
                    e.target.src = getImageUrl(null);
                  }}
                />
                
                <div className="dc-product-card-body">
                  <h2 className="dc-product-title">{product.name}</h2>
                  
                  <div className="dc-product-price">
                    {product.type === 'special_offer' && product.offerPrice ? (
                      <>
                        <span className="dc-current-price">{product.offerPrice?.toLocaleString()} LKR</span>
                        <span className="dc-original-price">{product.regularPrice?.toLocaleString()} LKR</span>
                      </>
                    ) : product.type === 'new_arrival' && product.offerPrice ? (
                      <>
                        <span className="dc-current-price">{product.offerPrice?.toLocaleString()} LKR</span>
                        <span className="dc-original-price">{product.regularPrice?.toLocaleString()} LKR</span>
                      </>
                    ) : (
                      <span className="dc-current-price">{product.price?.toLocaleString()} LKR</span>
                    )}
                  </div>
                  
                  <div className="dc-stock-wrapper">
                    <span
                      className={`dc-product-status ${
                        product.isPreOrder ? "dc-pre-order" : 
                        product.stock > 0 ? "dc-in-stock" : "dc-out-of-stock"
                      }`}
                    >
                      {product.isPreOrder ? "Pre-Order" : 
                       product.stock > 0 ? "In Stock" : "Out of Stock"}
                    </span>
                    {!product.isPreOrder && product.stock > 0 && (
                      <button
                        className="dc-add-to-cart"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddToCart(product);
                        }}
                      >
                        Add to Cart
                      </button>
                    )}
                    {product.isPreOrder && (
                      <button
                        className="dc-pre-order-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddToCart(product);
                        }}
                      >
                        Pre-Order
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {totalPages > 1 && (
        <div className="dc-pagination">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="dc-pagination-nav"
          >
            Previous
          </button>
          
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              className={page === currentPage ? "dc-active-page" : ""}
            >
              {page}
            </button>
          ))}
          
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="dc-pagination-nav"
          >
            Next
          </button>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default DynamicCategory;